import type {CopilotSpaceId} from '@github-ui/copilot-chat-types'
import {getCopilotSpacePath} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import {COPILOT_SPACES_QUERY_KEY, copilotSpacesQueryKey} from '@github-ui/copilot-shared-ui/spaces/fetch'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

export type Input = {starred: boolean}

/**
 * Toggles the starred state of a Copilot Space without requiring a CopilotChatManagerProvider.
 * Use this on surfaces (like the Spaces admin pages) that render outside the chat context.
 */
export function useStarCopilotSpace(space: CopilotSpaceId) {
  const queryClient = useQueryClient()

  const {
    mutateAsync: starCopilotSpace,
    isPending,
    ...rest
  } = useMutation({
    mutationFn: async (input: Input) => {
      const url = `${getCopilotSpacePath(space)}/stars`
      const method = input.starred ? 'POST' : 'DELETE'

      const response = await verifiedFetchJSON(url, {method})
      if (!response.ok) {
        throw new Error('Unable to update star.')
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: copilotSpacesQueryKey(space)})
      await queryClient.invalidateQueries({queryKey: COPILOT_SPACES_QUERY_KEY})
    },
  })

  return {
    starCopilotSpace,
    isLoading: isPending,
    ...rest,
  }
}
